/**
 * Jezik vmesnika (docs/ARHITEKTURA.md, 7).
 *
 * Jezik je nastavitev uporabnika (`users.locale`), ne brskalnika: ista oseba
 * dobi portal v istem jeziku na racunalniku in na telefonu. Pred prijavo
 * uporabnika se ni, zato se takrat upostevata nastavitev brskalnika.
 *
 * Prevedena so besedila, ki jih vidi **stranka**: prijava, pregled podjetja,
 * porocilo z razclenitvijo in sprejem vabila. Skrbniske strani (uporabniki,
 * uvoz, nastavitve razvrscanja, arhiv, posiljanja) ostanejo slovenske -
 * uporablja jih racunovodski servis. Izjema so Splosne nastavitve →
 * Kazalniki (`sn`): tam skrbnik ureja tudi angleska besedila, zato je stran
 * prevedena.
 */
import { PRIVZETI_JEZIK, vJezik, type GibanjeKazalnika, type Jezik } from '@koda/shared';
import { createContext, useContext } from 'react';

export interface Besedila {
  /** Splošne nastavitve → Kazalniki (skrbnik, prevedeno). */
  sn: {
    splosneNastavitve: string;
    naslov: string;
    podnaslov: string;
    beremNastavitve: string;
    napakaBranja: string;
    napakaShranjevanja: string;
    shrani: string;
    shranjujem: string;
    shranjeno: string;
    ponastavi: string;
    izklopljen: string;
    izklopljeno: string;
    prilagojeno: string;
    formula: string;
    formulaNiUrejljiva: string;
    /** Gumbi kataloga (graditelj kazalnikov). */
    ustvari: string;
    urediVGraditelju: string;
    kopiraj: string;
    kopirajNamig: string;
    izbrisi: string;
    izbrisiPotrdi: (naziv: string) => string;
    izbrisiDa: string;
    /** Motor kazalnika v katalogu (6.3, Katalog in formule). */
    motorVgrajen: string;
    motorFormula: string;
    izGraditelja: string;
    vklopZaVse: string;
    vklop: string;
    privzetoVklopljen: (vklopljen: boolean) => string;
    privzetoVklopljeno: (vklopljeno: boolean) => string;
    vklopljen: string;
    vklopljeno: string;
    izklopljenIzbira: string;
    izklopljenoIzbira: string;
    vrstniRed: string;
    vrstniRedNapaka: string;
    nazivSl: string;
    nazivEn: string;
    opisSl: string;
    opisEn: string;
    naslovSl: string;
    naslovEn: string;
    besediloSl: string;
    besediloEn: string;
    stopnja: string;
    privzetoStopnja: (stopnja: string) => string;
    vrstaTrend: string;
    vrstaRaven: string;
    pravilaOpozoril: string;
    nadomestki: (seznam: string) => string;
    brezNadomestkov: string;
    pragNapaka: (prag: string) => string;
    izkljuciGa: (pravila: string) => string;
    izkljuci: (pravila: string) => string;
    pragovi: Record<string, string>;
  };
  /** Glava in skupno. */
  znamka: string;
  /** Znamka v glavi: prvi del temen, drugi poudarjen (»Koda Reporting«). */
  znamkaPrvi: string;
  znamkaDrugi: string;
  /** Glavni meni: zavihki poročil, izbira podjetja, povezava skrbnika na podjetje. */
  porocila: string;
  /** Glavni zavihki menija (Pregled poslovanja, Finančni izkazi …). */
  glavniZavihki: string;
  vsaPodjetja: string;
  uvozInNastavitve: string;
  podnaslov: string;
  podjetja: string;
  uporabniki: string;
  posiljanja: string;
  arhiv: string;
  stanjeBaze: string;
  /** Glavni meni: splošne nastavitve (skrbnik). */
  nastavitve: string;
  skrbnik: string;
  zamenjajGeslo: string;
  odjava: string;
  staroGeslo: string;
  novoGeslo: string;
  zamenjaj: string;
  gesloZamenjano: string;
  gesloNiUspelo: string;
  jezikOznaka: string;
  berem: string;
  /** Prijava in vabilo. */
  prijava: string;
  ePosta: string;
  geslo: string;
  prijavljam: string;
  prijavaNiUspela: string;
  dostopOdpre: string;
  nastavitevGesla: string;
  vabiloNiVeljavno: string;
  vabiloZa: (email: string) => string;
  nastaviGeslo: string;
  shranjujem: string;
  gesloNiMogoce: string;
  vabilaNiMogoce: string;
  /** Novo geslo po povezavi od skrbnika. */
  novoGesloNaslov: string;
  ponastavitevNiVeljavna: string;
  ponastavitevZa: (email: string) => string;
  shraniNovoGeslo: string;
  ponastavitveNiMogoce: string;
  /** Seznam podjetij. */
  podjetijNiMogoce: string;
  brezPodjetijSkrbnik: string;
  brezPodjetijStranka: string;
  novoPodjetje: string;
  naziv: string;
  virKnjizb: string;
  uvozNiPodprt: (naziv: string) => string;
  ustvari: string;
  podjetjaNiMogoce: string;
  /** Pregled podjetja. */
  podjetje: string;
  poslovnoLeto: string;
  brezKnjizb: string;
  naloziIzvoz: string;
  knjizb: string;
  primerjalnoLeto: string;
  /** Izbira leta v poročilu samem. */
  letoPorocila: string;
  /** Namig na letnici v naslovu poročila. */
  zamenjajLeto: string;
  odMeseca: string;
  /** Imena mesecev za izbirnik razpona pri Primerjavi. */
  meseci: string[];
  doMeseca: string;
  /** Izbira mesecev: brez omejitve. */
  vsiMeseci: string;
  /** Naziv menija mesecev nad poročilom. */
  meseciNaziv: string;
  /** Mesec za pokritostjo uvoza. */
  niPodatkov: string;
  /** Namig v meniju mesecev pred prvim klikom. */
  meseciZaporedni: string;
  /** Namig v meniju mesecev po prvem kliku. */
  izberiKoncniMesec: string;
  skupina: string;
  namigLeta: string;
  porocilaZa: (leto: number) => string;
  brezPorocil: string;
  paketSpodaj: string;
  podatki: string;
  nastavitveRazvrscanja: string;
  namigNastavitve: string;
  nastavitveKazalnikov: string;
  namigNastavitveKazalnikov: string;
  nastavitveFcVc: string;
  namigNastavitveFcVc: string;
  nastavitvePartnerjev: string;
  namigNastavitvePartnerjev: string;
  projekti: string;
  nastavitevOdstotkov: string;
  namigNastavitevOdstotkov: string;
  projektiPregled: string;
  namigProjektiPregled: string;
  nastavitveProjektov: string;
  namigNastavitveProjektov: string;
  letNiMogoce: string;
  opisiPorocil: Record<string, string>;
  /** Porocilo. */
  napacenNaslov: string;
  racunamPorocilo: string;
  porociloNiMogoce: string;
  namigPorocila: string;
  izvoziPorocilo: string;
  izvoziVsa: string;
  pokaziPodrobneKonte: string;
  postavka: string;
  /** Naslov stolpca z nazivi vrstic pri Kazalnikih. */
  kazalnik: string;
  /** Zavihka Partnerji in RVC po kupcih ter njuna stran nastavitev (docs/ARHITEKTURA.md, 6.5). */
  pa: {
    namig: string;
    mesto: string;
    partner: string;
    niPartnerjev: string;
    brezTabel: string;
    izbiraKontov: string;
    privzete: string;
    poMeri: string;
    konti: string;
    namigKontov: string;
    vnesiKonto: string;
    /** Del vnosa kontov, ki ni konto (npr. »23x«). */
    napacniKonti: (konti: string) => string;
    /** RVC po kupcih: razvrstitev. */
    razvrstitev: string;
    najboljsiRvc: string;
    najslabsiRvc: string;
    /** Stran nastavitev: vrste dokumentov na kontih RVC. */
    vrstaDokumenta: string;
    knjizb: string;
    prodajnaVrednost: string;
    nabavnaVrednost: string;
    brezVrste: string;
    stejeVRvc: string;
    nacinVrst: string;
    vsiDokumenti: string;
    samoIzbrane: string;
    izberiVrsto: string;
    vrsteNaKontihNamig: string;
    niVrstNaKontih: string;
    /** Napaka v eni prednastavitvi na strani nastavitev. */
    napakaVrstice: (vrstica: string, opis: string) => string;
    stran: string;
    strani: { breme: string; dobro: string };
    pokazi: string;
    prikazi: string;
    prvih: (n: number) => string;
    nastavitve: string;
    /** Stran nastavitev. */
    naslov: string;
    podnaslov: string;
    samoBranje: string;
    prednastavitve: string;
    standardne: string;
    lastne: string;
    naziv: string;
    privzeta: string;
    namigPrivzeta: string;
    dodaj: string;
    odstrani: string;
    shrani: string;
    vrniPrivzete: string;
    shranjeno: string;
    napakaBranja: string;
    napakaShranjevanja: string;
    vrsteNaslov: string;
    vrsteNamig: string;
    vrste: string;
    kontiRvc: (prodajna: string, nabavna: string) => string;
    brezKontovRvc: string;
  };
  /** Zavihek Občutljivost strani Kazalniki (docs/ARHITEKTURA.md, 6.3). */
  ob: {
    zavihek: string;
    obdobje: string;
    celoLeto: (leto: number) => string;
    tekoceLeto: (obdobje: string) => string;
    podnaslovCelo: (leto: number) => string;
    podnaslovTekoce: (obdobje: string) => string;
    nerazvrsceni: (odstotek: string, znesek: string) => string;
    nadPragomNaslov: string;
    nadPragomBesedilo: (prag: string) => string;
    kontrola: (ebitda: string, cd: string) => string;
    niPodatkov: string;
    tockaNaslov: string;
    tocka: string;
    cistiPrihodki: string;
    rezerva: string;
    cistihPrihodkov: (odstotek: string) => string;
    stavekNiMogoce: string;
    stavekPokrito: string;
    stavekPrimanjkljaj: (znesek: string) => string;
    stavekRezerva: (odstotek: string) => string;
    opomba: string;
    legendaPrihodki: string;
    legendaKritje: string;
    legendaFiksni: string;
    podGrafom: string;
    tockaNaGrafu: string;
    dejanskiPrihodki: string;
    grafOpis: string;
    poglejIzracun: string;
    skrijIzracun: string;
    izracunCp: string;
    izracunV: string;
    izracunCm: string;
    izracunFe: string;
    izracunAm: string;
    izracunOr: string;
    izracunPoslovni: string;
    izracunOdstetiCp: string;
    celicaIzhodisce: string;
    grafPokrito: string;
    izracunDfIzguba: string;
    izracunDfDobicek: string;
    izracunF: string;
    izracunBe: string;
    izracunKontrola: string;
    matrikaNaslov: string;
    strosek: string;
    strosekIzbran: (naziv: string, delez: string) => string;
    vseFiksno: string;
    nacinKolicina: string;
    nacinCena: string;
    razlagaKolicina: string;
    razlagaCena: string;
    osKolicina: string;
    osCena: string;
    osStroska: (naziv: string) => string;
    izhodisce: string;
    podMatriko: (ebitda: string) => string;
    celicaNaslov: (vrstica: string, stolpec: string) => string;
    celicaPrihodki: (sprememba: string) => string;
    celicaOr: string;
    celicaFiksni: string;
    celicaOstali: string;
    celicaIzbrani: string;
    celicaEbitda: string;
    stroskiPoKontih: string;
    konto: string;
    naziv: string;
    znesek: string;
    vc: string;
    nerazvrsceno: string;
    zapri: string;
    variabilniDel: (delez: string) => string;
  };
  pk: {
    pregled: string;
    podrobno: string;
    kljucniZneski: string;
    kazalniki: string;
    vsiKazalniki: string;
    kajPomeni: string;
    pogledIzracun: string;
    skrijIzracun: string;
    letos: string;
    lani: string;
    razlika: string;
    formula: string;
    sestavine: string;
    poMesecih: string;
    grafPrihodkov: string;
    /** Podrobno (zasnova »Podrobno – splet«). */
    grafDobicka: string;
    grafDobickaPodnaslov: (leto: number, primerjalno: number | null) => string;
    dobicek: string;
    izguba: string;
    denarniTokPodnaslov: string;
    zacetekLeta: string;
    stanjeDenarja: string;
    razlikaVCeniPodnaslov: string;
    /** Referenčna črta grafa marže: razlika v ceni obdobja ÷ prodajna vrednost obdobja. */
    marzaObdobja: string;
    kontiRazlike: (nabavna: string, prodajna: string) => string;
    podrobnoNoga: (datum: string) => string;
    kratkaPrimerjava: string;
    ni: string;
    brezKazalnikov: string;
    proti: (kaj: string) => string;
    meseci: string;
    krat: string;
    namigTocke: (mesec: string, leto: number, znesek: string) => string;
    odstotneTocke: string;
    /** Okence »Gibanje« pri terjatvah, zalogah in obveznostih do dobaviteljev (6.3). */
    gibanje: string;
    /** Napis nad glavno številko: razlika med rastjo stanja in rastjo prometa. */
    gibanjeGlavno: string;
    /** »razlika +13,9 o. t.« pri stanju na zadnji dan. */
    gibanjeRazlika: (razlika: string) => string;
    /** Razlaga štirih podatkov okenca v »Kaj pomeni?«, v vrstnem redu prikaza. */
    gibanjePomen: (g: GibanjeKazalnika) => string[];
    /** »Poglej izračun«: razdelek Gibanje stanja z uporabljenimi številkami in razlago. */
    gibanjeStanja: string;
    gibanjeUvod: (obdobjeZdaj: string, obdobjePrej: string, rastStanja: string, promet: string, rastPrometa: string) => string;
    gibanjeRazmerje: (razlika: string, smer: 'vecje' | 'manjse' | 'enako') => string;
    gibanjeNaDan: (datumZdaj: string, datumPrej: string, rast: string, razlika: string | null) => string;
    gibanjeLeto: (odZacetka: string | null, lani: string | null) => string;
    gibanjeStolpci: { postavka: string; prej: string; zdaj: string; sprememba: string; protiPrometu: string };
    kakoSeRacuna: string;
    /** Razširjeni prikaz: kaj kazalnik pove in česa ne pove. */
    kajPove: string;
    mesec: string;
    zaPregled: string;
    niSignalov: string;
    preveri: string;
    steviloSignalov: (n: number) => string;
    /** Največja stroška (6.3). */
    nsPodnaslov: (obdobje: string, primerjalno: number | null) => string;
    nsDelez: (delez: string) => string;
    nsStrosek: string;
    nsPostavka: string;
    nsRast: string;
    nsDelezStolpec: string;
    /** Največji postavki celega primerjalnega leta (dejansko izbrano leto, ne nujno lani). */
    nsCeloLeto: (leto: number, seznam: string) => string;
    /** Telefon: »prihodki +11,4 %«, »18,0 % prihodkov«. */
    nsPrihodkiRast: (rast: string) => string;
    nsDelezKratko: (delez: string) => string;
    /** Za pregled: opomba ob naslovu, gumb Več/Manj. */
    zaPregledOpomba: string;
    vec: string;
    manj: string;
    pokaziRazlago: string;
    skrijRazlago: string;
    /** Veznik v opisu grafa (»2026 in 2025«). */
    in: string;
    /** Kartice, Gibanje, trak primerjave in noga (videz po zasnovi). */
    enotaMeseci: string;
    laniMalo: string;
    /** Dnevi vezave ob kartici obratnega kapitala, npr. »45 dni vezave«. */
    dniVezave: string;
    /** »lani 52« ob dneh vezave. */
    dniVezaveLani: (dni: string) => string;
    projekcijaNaLeto: string;
    gibanjePovprecnoStanje: string;
    primerjavaDo: (n: number) => string;
    primerjavaPodnaslov: (leto: number, primerjalno: number | null) => string;
    nogaOpomba: string;
    /** Glava strani Kazalniki. */
    glavaPodnaslov: (obdobje: string, primerjalno: number | null, stanje: string) => string;
    primerjava: string;
    izvozVExcel: string;
    /** Ustvarjanje vrednosti (6.3). */
    uvPodnaslov: (obdobje: string) => string;
    uvPodnaslovKratek: string;
    uvOpisGrafa: string;
    uvKosmategaDonosa: string;
    uvDodaneVrednosti: string;
    /** »1,1 % dodane vrednosti«; odstotek je že zapisan z enoto. */
    uvDelezDodane: (odstotek: string) => string;
    uvPrimanjkljaj: string;
    uvIzguba: string;
    uvBrezGrafa: string;
    /** Stavek pod grafom na telefonu. */
    uvStavek: (razvoj: string, odstotek: string, odplacila: string, primanjkljaj: boolean) => string;
    uvStavekIzguba: (ebit: string) => string;
    uvOdplacila: (odplacila: string, obresti: string, glavnice: string) => string;
    uvLegenda: { blago: string; dodana: string; delo: string; amortizacija: string; razvoj: string };
    uvIzracun: string;
    uvPrenosi: string;
    uvTemeljnica: (temeljnica: string) => string;
    uvNeprepoznaniPrenosi: (znesek: string) => string;
  };
  /** Graditelj kazalnikov (6.3). */
  gr: {
    naslov: string;
    podnaslov: string;
    odpri: string;
    nazaj: string;
    podjetje: string;
    leto: string;
    primerjalno: string;
    samoPredogled: string;
    beremMrezo: string;
    brezPodjetij: string;
    brezLet: string;
    stolpecC: string;
    formula: string;
    namigFormule: string;
    placeholder: string;
    prikaz: string;
    enota: string;
    decimalke: string;
    imenovalecPozitiven: string;
    skrijNic: string;
    projekcijaOd: string;
    pomen: string;
    predogled: string;
    racunam: string;
    vpisiFormulo: string;
    niPrikazan: string;
    iskanje: string;
    namigMreze: string;
    posebnost: string;
    razpri: (naziv: string) => string;
    vstavi: (koordinata: string) => string;
    odseki: Record<'ipi' | 'bs' | 'cf' | 'vloge' | 'izracunane', string>;
    enote: Record<'odstotek' | 'krat' | 'meseci' | 'eur', string>;
    napaka: (vrsta: string, mesto: string, podrobnost: string) => string;
    /** Shranjevanje v katalog. */
    shraniNaslov: string;
    koda: string;
    kodaNamig: string;
    kodaNeveljavna: string;
    nazivSl: string;
    nazivEn: string;
    opisSl: string;
    opisEn: string;
    skupina: string;
    dodajVPregled: string;
    shrani: string;
    shraniSpremembe: string;
    shranjujem: string;
    shranjeno: (naziv: string) => string;
    nazajVKatalog: string;
    urejam: (naziv: string) => string;
    kopija: (naziv: string) => string;
    niVMrezi: string;
    niKazalnika: (koda: string) => string;
    brezFormule: string;
    /** Formule ni bilo mogoče prenesti na mrežo drugega podjetja. */
    neprenesena: (formula: string) => string;
  };
  skupaj: string;
  vsaObdobja: string;
  namigCelice: string;
  namigCelicaSkupaj: string;
  /** Izbirnik dimenzije. */
  dimenzija: string;
  brezFiltra: string;
  vrednost: string;
  vse: string;
  prazno: string;
  vecElementov: string;
  izberiVecElementov: string;
  isci: string;
  niZadetkov: string;
  vRedu: string;
  preklici: string;
  filterZa: (naziv: string) => string;
  /** Razclenitev. */
  razclenitev: string;
  zapriRazclenitev: string;
  beremKnjizbe: string;
  niKnjizb: string;
  povzetek: (koliko: number, znesek: string) => string;
  prikazanihPrvih: (koliko: number) => string;
  pokaziIzlocene: string;
  prenesiKnjizbe: string;
  ponastaviSirine: string;
  namigSirine: string;
  sirinaStolpca: (stolpec: string) => string;
  knjizbeNapaka: string;
  glavaDatum: string;
  glavaKonto: string;
  glavaDokument: string;
  glavaPartner: string;
  glavaOpis: string;
  glavaZnesek: string;
  /** Izvirna vrstica knjižbe (samo skrbnik, docs/ARHITEKTURA.md, 5.6). */
  izvirnaVrstica: string;
  pokaziIzvirnoVrstico: string;
  beremIzvirnoVrstico: string;
  izvirnaVrsticaNapaka: string;
  virArhiv: string;
  virKnjizba: string;
  lokacijaXlsx: (zavihek: string, vrstica: number) => string;
  lokacijaCsv: (zapis: number) => string;
  izvirnaDatoteka: string;
  glavaStolpec: string;
  glavaVrednost: string;
  stolpcaNiBilo: string;
  zapriOkno: string;
  /** Stran, ki je samo za skrbnika. */
  niDostopa: string;
  straneUrejaServis: string;
}

const SL: Besedila = {
  znamka: 'Koda Reporting Portal',
  znamkaPrvi: 'Koda',
  znamkaDrugi: 'Reporting',
  porocila: 'Poročila',
  glavniZavihki: 'Glavni zavihki',
  vsaPodjetja: 'Vsa podjetja …',
  uvozInNastavitve: 'Uvoz in nastavitve',
  podnaslov: 'Pregled finančnih poročil',
  podjetja: 'Podjetja',
  uporabniki: 'Uporabniki',
  posiljanja: 'Pošiljanja',
  arhiv: 'Arhiv',
  stanjeBaze: 'Stanje baze',
  nastavitve: 'Nastavitve',
  skrbnik: 'skrbnik',
  zamenjajGeslo: 'Zamenjaj geslo',
  odjava: 'Odjava',
  staroGeslo: 'Staro geslo',
  novoGeslo: 'Novo geslo (vsaj 10 znakov)',
  zamenjaj: 'Zamenjaj',
  gesloZamenjano: 'Geslo je zamenjano. Druge naprave so odjavljene.',
  gesloNiUspelo: 'Gesla ni bilo mogoče zamenjati.',
  jezikOznaka: 'Jezik',
  berem: 'Berem …',
  prijava: 'Prijava',
  ePosta: 'E-pošta',
  geslo: 'Geslo',
  prijavljam: 'Prijavljam …',
  prijavaNiUspela: 'Prijava ni uspela.',
  dostopOdpre: 'Dostop odpre računovodski servis. Če gesla ne poznate, se obrnite nanj.',
  nastavitevGesla: 'Nastavitev gesla',
  vabiloNiVeljavno: 'Vabilo ni veljavno ali je poteklo.',
  vabiloZa: (email) => `Vabilo za ${email}. Nastavite geslo, s katerim se boste prijavljali.`,
  nastaviGeslo: 'Nastavi geslo in se prijavi',
  shranjujem: 'Shranjujem …',
  gesloNiMogoce: 'Gesla ni bilo mogoče nastaviti.',
  vabilaNiMogoce: 'Vabila ni bilo mogoče prebrati.',
  novoGesloNaslov: 'Novo geslo',
  ponastavitevNiVeljavna: 'Povezava za novo geslo ni veljavna ali je potekla. Prosite računovodski servis za novo.',
  ponastavitevZa: (email) => `Novo geslo za ${email}. Ob shranjevanju boste odjavljeni z vseh drugih naprav.`,
  shraniNovoGeslo: 'Shrani novo geslo in se prijavi',
  ponastavitveNiMogoce: 'Povezave za novo geslo ni bilo mogoče prebrati.',
  podjetijNiMogoce: 'Podjetij ni bilo mogoče prebrati.',
  brezPodjetijSkrbnik: 'Podjetja še ni. Ustvari ga spodaj in nato naloži izvoz glavne knjige.',
  brezPodjetijStranka: 'Dostopa do nobenega podjetja še nimate. Obrnite se na računovodski servis.',
  novoPodjetje: 'Novo podjetje',
  naziv: 'Naziv',
  virKnjizb: 'Vir knjižb',
  uvozNiPodprt: (naziv) => `${naziv} (uvoz še ni podprt)`,
  ustvari: 'Ustvari',
  podjetjaNiMogoce: 'Podjetja ni bilo mogoče ustvariti.',
  podjetje: 'Podjetje',
  poslovnoLeto: 'Poslovno leto',
  brezKnjizb: 'Za to podjetje še ni knjižb.',
  naloziIzvoz: 'Naloži izvoz glavne knjige',
  knjizb: 'knjižb',
  primerjalnoLeto: 'Primerjalno leto',
  letoPorocila: 'Leto',
  zamenjajLeto: 'Klik na letnico zamenja leto poročila',
  odMeseca: 'Od meseca',
  meseci: ['januar', 'februar', 'marec', 'april', 'maj', 'junij', 'julij', 'avgust', 'september', 'oktober', 'november', 'december'],
  doMeseca: 'Do meseca',
  vsiMeseci: '(Vsi meseci)',
  meseciNaziv: 'Meseci',
  niPodatkov: 'ni podatkov',
  meseciZaporedni: 'Klikni prvi in zadnji mesec – meseci vmes se izberejo sami.',
  izberiKoncniMesec: 'Klikni še zadnji mesec.',
  skupina: 'Skupina',
  namigLeta: 'Naenkrat sta na voljo največ dve leti: zadnje in eno primerjalno.',
  porocilaZa: (leto) => `Poročila za ${String(leto)}`,
  brezPorocil: 'Za to podjetje ni vklopljeno nobeno poročilo.',
  paketSpodaj: ' Paket nastavite spodaj.',
  podatki: 'Podatki',
  nastavitveRazvrscanja: 'Nastavitve razvrščanja',
  namigNastavitve: 'Skupine, razponi kontov in postavke, kot veljajo za to podjetje.',
  nastavitveKazalnikov: 'Nastavitve kazalnikov',
  namigNastavitveKazalnikov: 'Konti za razliko v ceni in vrstice, iz katerih kazalniki berejo.',
  nastavitveFcVc: 'Fiksni in variabilni stroški',
  namigNastavitveFcVc: 'Delež variabilnih stroškov po kontih za analizo občutljivosti.',
  nastavitvePartnerjev: 'Partnerji in RVC po kupcih',
  namigNastavitvePartnerjev: 'Konti za promet po partnerjih in vrste dokumentov za razliko v ceni po kupcih.',
  projekti: 'Projekti',
  nastavitevOdstotkov: 'Nastavitev odstotkov',
  namigNastavitevOdstotkov: 'Deleži projektov na osnovnih sredstvih za delitev amortizacije.',
  projektiPregled: 'Pregled projektov',
  namigProjektiPregled: 'Poraba amortizacije po projektih, preostanek in poraba po mesecih.',
  nastavitveProjektov: 'Nastavitve projektov',
  namigNastavitveProjektov: 'Šifrant projektov, register osnovnih sredstev, temeljnica in zaključek meseca.',
  letNiMogoce: 'Let ni bilo mogoče prebrati.',
  opisiPorocil: {
    hitri: 'Poenostavljen pregled',
    rezultat: 'Promet po mesecih',
    'bs-s': 'Stanja konec meseca',
    bs: 'Začetno stanje in spremembe',
    cf: 'Denarni tok po mesecih',
    primerjava: 'Letos in lani',
    kazalniki: 'Ključni kazalniki, občutljivost',
    partnerji: 'Največji kupci in dobavitelji',
    'rvc-kupci': 'Razlika v ceni po kupcih',
  },
  napacenNaslov: 'Napačen naslov poročila.',
  racunamPorocilo: 'Računam poročilo …',
  porociloNiMogoce: 'Poročila ni bilo mogoče prebrati.',
  namigPorocila:
    'Klik na številko pokaže knjižbe, iz katerih je sestavljena — tudi pri skupini in pri skupni vrstici. Klik na naziv zloži ali razširi raven.',
  izvoziPorocilo: 'Izvozi poročilo v Excel',
  izvoziVsa: 'Izvozi vsa poročila (vsako v svoj zavihek)',
  pokaziPodrobneKonte: 'Pokaži podrobne konte',
  postavka: 'Postavka',
  kazalnik: 'Kazalnik',
  sn: {
    splosneNastavitve: 'Splošne nastavitve',
    naslov: 'Kazalniki',
    podnaslov:
      'Velja za vsa podjetja. Prazno polje pomeni privzeto vrednost iz aplikacije; shranijo se samo spremembe, zato jih posodobitev aplikacije ne povozi. Podjetje lahko posamezen kazalnik zase vklopi ali izklopi na strani Nastavitve kazalnikov.',
    beremNastavitve: 'Berem nastavitve …',
    napakaBranja: 'Nastavitev ni bilo mogoče prebrati.',
    napakaShranjevanja: 'Nastavitve ni bilo mogoče shraniti.',
    shrani: 'Shrani',
    shranjujem: 'Shranjujem …',
    shranjeno: 'Shranjeno.',
    ponastavi: 'Ponastavi na privzeto',
    izklopljen: 'izklopljen',
    izklopljeno: 'izklopljeno',
    prilagojeno: 'prilagojeno',
    formula: 'Formula',
    formulaNiUrejljiva: 'formula je del aplikacije in se tu ne ureja',
    ustvari: '+ Ustvari svoj kazalnik',
    urediVGraditelju: 'Uredi v graditelju',
    kopiraj: 'Kopiraj in prilagodi',
    kopirajNamig: 'Vgrajen izračun ostane; kopija je nov kazalnik s formulo, ki ga lahko spremenite.',
    izbrisi: 'Izbriši kazalnik',
    izbrisiPotrdi: (naziv) => `Kazalnik »${naziv}« se izbriše skupaj z nastavitvami pri vseh podjetjih. Res izbrišem?`,
    izbrisiDa: 'Da, izbriši',
    motorVgrajen: 'vgrajen izračun',
    motorFormula: 'formula',
    izGraditelja: 'sestavljeno v graditelju',
    vklopZaVse: 'Vklop za vsa podjetja',
    vklop: 'Vklop',
    privzetoVklopljen: (v) => `Privzeto (${v ? 'vklopljen' : 'izklopljen'})`,
    privzetoVklopljeno: (v) => `Privzeto (${v ? 'vklopljeno' : 'izklopljeno'})`,
    vklopljen: 'Vklopljen',
    vklopljeno: 'Vklopljeno',
    izklopljenIzbira: 'Izklopljen',
    izklopljenoIzbira: 'Izklopljeno',
    vrstniRed: 'Vrstni red',
    vrstniRedNapaka: 'Vrstni red mora biti celo število.',
    nazivSl: 'Naziv (SL)',
    nazivEn: 'Naziv (EN)',
    opisSl: 'Opis – »Kaj pomeni?« (SL)',
    opisEn: 'Opis – »Kaj pomeni?« (EN)',
    naslovSl: 'Naslov (SL)',
    naslovEn: 'Naslov (EN)',
    besediloSl: 'Besedilo (SL)',
    besediloEn: 'Besedilo (EN)',
    stopnja: 'Stopnja (vpliva na vrstni red)',
    privzetoStopnja: (s) => `Privzeto (${s})`,
    vrstaTrend: 'primerjava z lani',
    vrstaRaven: 'vrednost proti pragu',
    pravilaOpozoril: 'Pravila opozoril',
    nadomestki: (seznam) => `Dovoljeni nadomestki v besedilu: ${seznam}. Številke se vstavijo v slovenskem zapisu.`,
    brezNadomestkov: 'brez',
    pragNapaka: (prag) => `${prag}: vpiši število (decimalna vejica, brez ločil tisočic).`,
    izkljuciGa: (p) => `Ne pokaže se, kadar se sproži ${p} (izklopljeno pravilo ne izključuje).`,
    izkljuci: (p) => `Kadar se sproži, se ne pokaže ${p}.`,
    pragovi: {
      prag: 'Prag',
      mocnejsi_prag: 'Močnejši prag (stopnja »warning«)',
      najmanj_mesecev: 'Najmanj mesecev obdobja',
      razmerje_pod: 'Razmerje pod (%)',
      povecanje: 'Povečanje glede na lani',
      najmanjse_razmerje: 'Najmanjše razmerje',
      poslabsanje_mesecev_prihodkov: 'Zmanjšanje (delež povprečnega mesečnega prihodka od prodaje)',
      razlika_rasti: 'Razlika rasti (odstotne točke)',
      najmanj_mesecev_prihodkov: 'Najmanj mesecev prihodkov',
      najmanj_mesecev_prometa: 'Najmanj mesecev prometa',
    },
  },
  pa: {
    namig: 'Klik na znesek pokaže knjižbe partnerja; lani je isto obdobje primerjalnega leta.',
    mesto: 'Mesto',
    partner: 'Partner',
    niPartnerjev: 'V izbranem obdobju na izbranih kontih ni knjižb.',
    brezTabel: 'Za izbrano obdobje ni tabele.',
    izbiraKontov: 'Izbira kontov',
    privzete: 'Privzete',
    poMeri: 'Po meri …',
    konti: 'Konti',
    namigKontov: 'Predpone kontov, ločene z vejico, npr. 22, 23 ali 7600.',
    vnesiKonto: 'Vnesi vsaj en konto (same števke).',
    napacniKonti: (konti) => `Neveljaven konto: ${konti}. Konto ima od 1 do 6 števk, npr. 22 ali 7600.`,
    razvrstitev: 'Razvrstitev',
    najboljsiRvc: 'Najboljši RVC',
    najslabsiRvc: 'Najslabši RVC',
    vrstaDokumenta: 'Vrsta dokumenta',
    knjizb: 'Knjižb',
    prodajnaVrednost: 'Prodajna vrednost',
    nabavnaVrednost: 'Nabavna vrednost',
    brezVrste: '(brez vrste)',
    stejeVRvc: 'Šteje v RVC',
    nacinVrst: 'Kateri dokumenti štejejo v RVC',
    vsiDokumenti: 'Vsi dokumenti (tudi nabave)',
    samoIzbrane: 'Samo izbrane vrste',
    izberiVrsto: 'Izberi vsaj eno vrsto dokumenta ali možnost »Vsi dokumenti«.',
    vrsteNaKontihNamig:
      'Vrste dokumentov, ki so v knjižbah na kontih razlike v ceni (vsa uvožena leta). Obkljukaj prodajo in nabavno vrednost prodanega blaga; nabav (npr. prejeti računi) ne – nosijo dobavitelja, ne kupca.',
    niVrstNaKontih: 'Na kontih razlike v ceni še ni knjižb.',
    napakaVrstice: (vrstica, opis) => `Prednastavitev »${vrstica}«: ${opis}`,
    stran: 'Stran',
    strani: { breme: 'v breme', dobro: 'v dobro' },
    pokazi: 'Pokaži',
    prikazi: 'Prikaži',
    prvih: (n) => `prvih ${String(n)}`,
    nastavitve: 'Nastavitve zavihka',
    naslov: 'Partnerji in RVC po kupcih',
    podnaslov:
      'Zavihek Partnerji sešteje izbrano stran knjižb na izbranih kontih po partnerju. Prednastavitve so hitre izbire nad tabelo; privzeto odprte (največ dve) se pokažejo brez izbire. Zavihek RVC po kupcih bere konte razlike v ceni iz nastavitev kazalnikov, vrste dokumentov pa so nastavljene tu.',
    samoBranje: 'Nastavitve lahko spreminja skrbnik podjetja ali računovodski servis.',
    prednastavitve: 'Prednastavitve zavihka Partnerji',
    standardne: 'Podjetje uporablja privzete prednastavitve iz standardne sheme. Ko jih shranite, veljajo vaše.',
    lastne: 'Podjetje ima svoje prednastavitve.',
    naziv: 'Naziv',
    privzeta: 'Privzeto odprta',
    namigPrivzeta: 'Privzeto odprti sta lahko največ dve prednastavitvi.',
    dodaj: 'Dodaj prednastavitev',
    odstrani: 'Odstrani',
    shrani: 'Shrani',
    vrniPrivzete: 'Vrni privzete',
    shranjeno: 'Shranjeno.',
    napakaBranja: 'Nastavitev ni bilo mogoče prebrati.',
    napakaShranjevanja: 'Nastavitev ni bilo mogoče shraniti.',
    vrsteNaslov: 'RVC po kupcih: vrste dokumentov',
    vrsteNamig:
      'Vrste dokumentov, ki štejejo v razliko v ceni po kupcih, ločene z vejico (pri Pantheonu npr. 420 izdani račun in 481 nabavna vrednost prodanega blaga). Prazno pomeni vse dokumente.',
    vrste: 'Vrste dokumentov',
    kontiRvc: (prodajna, nabavna) => `Konti razlike v ceni (nastavitve kazalnikov): prodajna vrednost ${prodajna}, nabavna vrednost ${nabavna}.`,
    brezKontovRvc: 'Konti razlike v ceni še niso nastavljeni; nastavi jih računovodski servis v nastavitvah kazalnikov.',
  },
  ob: {
    zavihek: 'Občutljivost',
    obdobje: 'Obdobje',
    celoLeto: (leto) => `Zadnje celo leto (${String(leto)})`,
    tekoceLeto: (obdobje) => `Tekoče leto (${obdobje})`,
    podnaslovCelo: (leto) => `Zadnje celo leto ${String(leto)} (1. 1.–31. 12.)`,
    podnaslovTekoce: (obdobje) => `${obdobje}, brez preračuna na leto`,
    nerazvrsceni: (odstotek, znesek) =>
      `${odstotek} stroškov (${znesek}) še nima določene razvrstitve na fiksni in variabilni del. V izračunu so obravnavani kot fiksni, zato so rezultati približni. Priporočamo, da razvrstitev pregledate skupaj s svetovalcem.`,
    nadPragomNaslov: 'Analiza še ni dovolj nastavljena',
    nadPragomBesedilo: (prag) =>
      `Več kot ${prag} stroškov tega obdobja še nima določene razvrstitve na fiksni in variabilni del, zato ocene točke preloma in matrike ne prikažemo. Razvrstitev uredi svetovalec.`,
    kontrola: (ebitda, cd) =>
      `Izračuna ni mogoče prikazati: vhodni podatki se ne ujemajo z izkazom poslovnega izida (razlika EBITDA ${ebitda}, celotni dobiček ${cd}). Obvestite svetovalca.`,
    niPodatkov: 'Za analizo občutljivosti ni podatkov.',
    tockaNaslov: 'Ocenjena točka preloma',
    tocka: 'Ocenjena točka preloma',
    cistiPrihodki: 'Čisti prihodki obdobja',
    rezerva: 'Varnostna rezerva',
    cistihPrihodkov: (odstotek) => `${odstotek} čistih prihodkov`,
    stavekNiMogoce: 'Točke preloma ni mogoče oceniti: variabilni stroški so enaki ali večji od prihodkov.',
    stavekPokrito:
      'Po predpostavkah tega modela drugi prihodki ter finančni in drugi učinki že pokrijejo fiksni del; ocenjena točka preloma je 0 €.',
    stavekPrimanjkljaj: (znesek) => `Za dosego točke preloma bi morali biti čisti prihodki višji za ${znesek}.`,
    stavekRezerva: (odstotek) => `Varnostna rezerva znaša ${odstotek} čistih prihodkov obdobja.`,
    opomba:
      'Ocena temelji na razdelitvi stroškov na fiksne in variabilne; upošteva amortizacijo ter finančne in druge prihodke in odhodke.',
    legendaPrihodki: 'prihodki',
    legendaKritje: 'potrebno kritje',
    legendaFiksni: 'fiksni del (neto)',
    podGrafom:
      'Potrebno kritje = fiksni stroški, amortizacija ter finančni in drugi učinki, zmanjšani za druge prihodke, plus variabilni stroški.',
    tockaNaGrafu: 'Točka preloma',
    dejanskiPrihodki: 'dejanski prihodki',
    grafOpis: 'Graf točke preloma: prihodki in potrebno kritje glede na čiste prihodke',
    poglejIzracun: 'Poglej izračun',
    skrijIzracun: 'Skrij izračun',
    izracunCp: 'Čisti prihodki (CP)',
    izracunV: 'Variabilni stroški (V)',
    izracunCm: 'Prispevek na 1 € prihodkov (1 − V / CP)',
    izracunFe: 'Fiksni stroški v EBITDA',
    izracunAm: '+ amortizacija',
    izracunOr: '− drugi poslovni prihodki',
    izracunPoslovni: 'poslovni prihodki',
    izracunOdstetiCp: '− čisti prihodki',
    celicaIzhodisce: 'izhodišče: čisti prihodki obdobja',
    grafPokrito:
      'Potrebno kritje je pri vseh prihodkih nižje od prihodkov, zato se črti na grafu ne sekata; ocenjena točka preloma je 0 €.',
    izracunDfIzguba: '+ izguba iz financiranja in drugih dejavnosti',
    izracunDfDobicek: '− dobiček iz financiranja in drugih dejavnosti',
    izracunF: '= fiksni del (F)',
    izracunBe: 'Točka preloma = F / prispevek',
    izracunKontrola: 'Kontrola: CP × prispevek − F = celotni dobiček',
    matrikaNaslov: 'Matrika EBITDA',
    strosek: 'Strošek',
    strosekIzbran: (naziv, delez) => `${naziv} (variabilni del ${delez})`,
    vseFiksno: 'Izbrani strošek je v celoti fiksen; sprememba cene na enoto nanj ne vpliva.',
    nacinKolicina: 'Sprememba količine',
    nacinCena: 'Sprememba cene',
    razlagaKolicina: 'Prodamo več ali manj po istih cenah; variabilni stroški se spremenijo sorazmerno.',
    razlagaCena: 'Prodamo enako količino po višji ali nižji ceni; stroški ostanejo enaki.',
    osKolicina: 'Sprememba količine prodaje',
    osCena: 'Sprememba prodajne cene',
    osStroska: (naziv) => `Sprememba cene na enoto: ${naziv}`,
    izhodisce: 'izhodišče',
    podMatriko: (ebitda) =>
      `EBITDA obdobja, pod njo razlika do izhodišča (${ebitda}). Sprememba cene na enoto vpliva samo na variabilni del izbranega stroška. Klik na celico pokaže izračun.`,
    celicaNaslov: (vrstica, stolpec) => `Izračun celice: prihodki ${vrstica}, cena stroška ${stolpec}`,
    celicaPrihodki: (sprememba) => `Čisti prihodki × (1 ${sprememba})`,
    celicaOr: '+ drugi poslovni prihodki',
    celicaFiksni: '− fiksni del stroškov',
    celicaOstali: '− variabilni del ostalih stroškov',
    celicaIzbrani: '− variabilni del izbranega stroška',
    celicaEbitda: '= EBITDA',
    stroskiPoKontih: 'Stroški po kontih',
    konto: 'Konto',
    naziv: 'Naziv',
    znesek: 'Znesek',
    vc: 'VC',
    nerazvrsceno: 'nerazvrščeno',
    zapri: 'Zapri',
    variabilniDel: (delez) => `VC ${delez}`,
  },
  pk: {
    pregled: 'Pregled',
    podrobno: 'Podrobno',
    kljucniZneski: 'Ključni zneski',
    kazalniki: 'Kazalniki',
    vsiKazalniki: 'Vsi kazalniki in tabele',
    kajPomeni: 'Kaj pomeni?',
    pogledIzracun: 'Poglej izračun',
    skrijIzracun: 'Zapri izračun',
    letos: 'Letos',
    lani: 'Lani',
    razlika: 'Razlika',
    formula: 'Formula',
    sestavine: 'Sestavine',
    poMesecih: 'Po mesecih',
    grafPrihodkov: 'Prihodki po mesecih',
    grafDobicka: 'Celotni dobiček po mesecih',
    grafDobickaPodnaslov: (leto, primerjalno) =>
      primerjalno === null ? `Stolpec ${String(leto)}` : `Stolpec ${String(leto)}, črtica ${String(primerjalno)}`,
    dobicek: 'dobiček',
    izguba: 'izguba',
    denarniTokPodnaslov: 'Stanje denarja na koncu meseca in tokovi po dejavnostih, v EUR',
    zacetekLeta: '1. 1.',
    stanjeDenarja: 'Stanje denarja',
    razlikaVCeniPodnaslov: 'Profitna marža po mesecih',
    marzaObdobja: 'Marža obdobja',
    kontiRazlike: (nabavna, prodajna) => `Konti, nastavljeni pri podjetju: nabavna ${nabavna} · prodajna ${prodajna}`,
    podrobnoNoga: (datum) =>
      `Številke izhajajo iz glavne knjige, stanje knjižb ${datum}. Temeljnice zapiranja leta, nastavljene pri podjetju, so izločene iz izkaza poslovnega izida. Klik na številko odpre knjižbe, iz katerih je sestavljena.`,
    kratkaPrimerjava: 'Kratka primerjava z lanskim letom',
    ni: '—',
    brezKazalnikov: 'Za to leto ni podatkov za kazalnike.',
    proti: (kaj) => `proti ${kaj}`,
    meseci: 'mes.',
    krat: '×',
    namigTocke: (mesec, leto, znesek) => `${mesec} ${String(leto)}: ${znesek} — klik pokaže knjižbe`,
    odstotneTocke: 'o. t.',
    gibanje: 'Gibanje',
    gibanjeGlavno: 'Rast stanja proti rasti prometa',
    gibanjeRazlika: (razlika) => `razlika ${razlika}`,
    gibanjeStanja: 'Gibanje stanja',
    gibanjeUvod: (obdobjeZdaj, obdobjePrej, rastStanja, promet, rastPrometa) =>
      `Povprečno stanje na koncu mesecev se je v obdobju ${obdobjeZdaj} glede na ${obdobjePrej} spremenilo za ${rastStanja}, ${promet} pa za ${rastPrometa}.`,
    gibanjeRazmerje: (razlika, smer) =>
      smer === 'enako'
        ? 'Rasti sta enaki: v razmerju do prometa je stanje enako kot lani.'
        : `Razlika obeh sprememb je ${razlika}: v razmerju do prometa je stanje ${smer === 'vecje' ? 'večje' : 'manjše'} kot lani.`,
    gibanjeNaDan: (datumZdaj, datumPrej, rast, razlika) =>
      `Stanje na zadnji dan (${datumZdaj}) se je glede na ${datumPrej} spremenilo za ${rast}${razlika === null ? '' : ` (${razlika} glede na rast prometa)`}.`,
    gibanjeLeto: (odZacetka, lani) =>
      [odZacetka === null ? null : `Od začetka leta se je stanje spremenilo za ${odZacetka}`, lani === null ? null : `lani v celem letu za ${lani}`]
        .filter((d) => d !== null)
        .join(', ') + '.',
    gibanjeStolpci: { postavka: 'Postavka', prej: 'Prej', zdaj: 'Zdaj', sprememba: 'Sprememba', protiPrometu: 'Glede na rast prometa' },
    kakoSeRacuna: 'Kako se računa',
    gibanjePomen: (g) => [
      `${g.povprecje.naziv}: rast povprečja stanj na konec vsakega meseca, letos in lani v istih mesecih, primerjana z rastjo prometa (${g.nazivPrometa}). Velika številka je razlika obeh rasti v odstotnih točkah. Povprečje ni občutljivo na en sam dan – na primer na večje plačilo tik pred koncem meseca –, zato je to glavni podatek.`,
      `${g.stanje.naziv}: stanje na zadnji dan obdobja proti istemu dnevu lani in razlika do rasti prometa. Je tu, ker ga lahko preverite v bilanci stanja; en dan pa lahko močno spremeni sliko.`,
      `${g.odZacetka.naziv}: kako se je stanje spremenilo letos, od otvoritve do zadnjega meseca. Pri sezonskem poslovanju je sprememba lahko velika tudi brez posebnega razloga.`,
      `${g.lani.naziv}: kako se je stanje spremenilo v celem lanskem letu, od otvoritve do 31. 12. – za občutek, ali je letošnje gibanje običajno.`,
    ],
    kajPove: 'Kaj pove in česa ne pove',
    mesec: 'Mesec',
    zaPregled: 'Za pregled',
    niSignalov: 'Ni signalov za pregled.',
    preveri: 'Preveri',
    steviloSignalov: (n) => `${String(n)} ${n === 1 ? 'signal' : n === 2 ? 'signala' : n <= 4 ? 'signali' : 'signalov'}`,
    nsPodnaslov: (obdobje, primerjalno) =>
      primerjalno === null ? obdobje : `${obdobje}, rast proti enakemu obdobju ${String(primerjalno)}`,
    nsDelez: (delez) => `${delez} % poslovnih prihodkov`,
    nsStrosek: 'Strošek',
    nsPostavka: 'Postavka',
    nsRast: 'Rast',
    nsDelezStolpec: 'Delež v prihodkih',
    nsCeloLeto: (leto, seznam) => (seznam === '' ? `Celo leto ${String(leto)}` : `Celo leto ${String(leto)}: ${seznam}`),
    nsPrihodkiRast: (rast) => `prihodki ${rast}`,
    nsDelezKratko: (delez) => `${delez} % prihodkov`,
    enotaMeseci: 'meseca',
    laniMalo: 'lani',
    dniVezave: 'dni vezave',
    dniVezaveLani: (dni) => `lani ${dni}`,
    projekcijaNaLeto: 'projekcija na leto',
    gibanjePovprecnoStanje: 'Povprečno stanje',
    primerjavaDo: (n) => `Primerjava do ${['januarja', 'februarja', 'marca', 'aprila', 'maja', 'junija', 'julija', 'avgusta', 'septembra', 'oktobra', 'novembra', 'decembra'][n - 1] ?? String(n)}`,
    primerjavaPodnaslov: (leto, primerjalno) =>
      primerjalno === null
        ? `${String(leto)}, od začetka leta.`
        : `${String(leto)} proti ${String(primerjalno)}, enako obdobje. Zelena puščica pomeni boljši izid, rdeča slabšega – pri stroških tudi.`,
    nogaOpomba: 'Klik na številko odpre knjižbe, iz katerih je sestavljena. Zneski na ploščicah so zaokroženi na evre.',
    zaPregledOpomba: 'Kazalniki so pripomoček za pregled poslovanja, ne ocena podjetja.',
    vec: 'Več',
    manj: 'Manj',
    pokaziRazlago: 'Pokaži razlago',
    skrijRazlago: 'Skrij razlago',
    in: 'in',
    glavaPodnaslov: (obdobje, primerjalno, stanje) =>
      `${obdobje}${primerjalno === null ? '' : `, primerjalno leto ${String(primerjalno)}`} · stanje knjižb ${stanje}`,
    primerjava: 'Primerjava',
    izvozVExcel: 'Izvoz v Excel',
    uvPodnaslov: (obdobje) =>
      `Kako se kosmati donos razdeli do zneska, ki podjetju ostane za razvoj · ${obdobje.charAt(0).toLocaleLowerCase('sl')}${obdobje.slice(1)}`,
    uvPodnaslovKratek: 'Od kosmatega donosa do zneska za razvoj',
    uvOpisGrafa: 'Ustvarjanje vrednosti: kosmati donos, dodana vrednost, EBIT in znesek za razvoj',
    uvKosmategaDonosa: 'kosmatega donosa',
    uvDodaneVrednosti: 'dodane vrednosti',
    uvDelezDodane: (odstotek) => `${odstotek} dodane vrednosti`,
    uvPrimanjkljaj: 'Primanjkljaj',
    uvIzguba: 'Izguba iz poslovanja',
    uvBrezGrafa: 'Razdelitve ni mogoče narisati, ker je dodana vrednost ali eden od njenih delov negativen. Zneski so v izračunu.',
    uvStavek: (razvoj, odstotek, odplacila, primanjkljaj) =>
      primanjkljaj
        ? `Odplačila dolga ${odplacila} presežejo EBIT: primanjkljaj ${razvoj} (${odstotek} dodane vrednosti).`
        : `Za razvoj ostane ${razvoj} (${odstotek} dodane vrednosti). Odplačila dolga ${odplacila}.`,
    uvStavekIzguba: (ebit) => `EBIT je negativen (${ebit}): izguba iz poslovanja, za odplačila dolga in razvoj ne ostane nič.`,
    uvOdplacila: (odplacila, obresti, glavnice) => `Odplačila dolga ${odplacila}: obresti ${obresti} · glavnice ${glavnice}`,
    uvLegenda: {
      blago: 'Blago, material, storitve',
      dodana: 'Dodana vrednost, EBIT',
      delo: 'Delo, odplačila dolga',
      amortizacija: 'Amortizacija',
      razvoj: 'Za razvoj',
    },
    uvIzracun: 'Izračun',
    uvPrenosi:
      'Glavnice so promet v breme na kontih finančnih obveznosti brez prenosov med dolgoročnim in kratkoročnim delom v isti temeljnici. Prenos je izračunan na temeljnico, zato ni vsota knjižb: klik na promet v breme pokaže knjižbe, prenosi so navedeni po temeljnicah.',
    uvTemeljnica: (temeljnica) => `Temeljnica ${temeljnica}`,
    uvNeprepoznaniPrenosi: (znesek) =>
      `Vir podatkov nima številke temeljnice, zato prenosi med dolgoročnim in kratkoročnim delom niso prepoznani in so šteti med glavnice. Takih prenosov je lahko največ ${znesek}.`,
  },
  gr: {
    naslov: 'Graditelj kazalnikov',
    podnaslov:
      'Formulo napišete kot v Excelu ali jo sestavite s klikanjem po celicah. Stolpec A je tekoče obdobje, B isto obdobje primerjalnega leta, C konec primerjalnega leta. Rezultat se sproti izračuna na izbranem podjetju.',
    odpri: 'Graditelj kazalnikov',
    nazaj: 'Kazalniki',
    podjetje: 'Predogled na podjetju',
    leto: 'Leto',
    primerjalno: 'Primerjalno leto',
    samoPredogled: 'Podjetje je samo za predogled. Kazalnik velja za vsa podjetja.',
    beremMrezo: 'Berem vrstice …',
    brezPodjetij: 'Ni podjetij za predogled.',
    brezLet: 'Podjetje nima uvoženih knjižb.',
    stolpecC: 'Pokaži stolpec C (konec primerjalnega leta)',
    formula: 'Formula',
    namigFormule:
      'Dovoljeno: + − * /, oklepaji in SUM(A1;A3;A8) z naštetimi celicami (brez razponov). Klik na celico vpiše njeno oznako na mesto kazalke. Decimalna vejica, npr. 0,5.',
    placeholder: '=A7/A1*100  ali kliknite celico v tabeli',
    prikaz: 'Prikaz',
    enota: 'Enota',
    decimalke: 'Decimalke',
    imenovalecPozitiven: 'Imenovalec mora biti pozitiven (sicer vrednosti ni)',
    skrijNic: 'Skrij kazalnik, kadar so vsa stanja v formuli nič',
    projekcijaOd: 'Projekcija na leto od meseca',
    pomen: 'Celice v formuli',
    predogled: 'Predogled',
    racunam: 'Računam …',
    vpisiFormulo: 'Vpišite formulo ali kliknite celico v tabeli.',
    niPrikazan: 'Na strani Pregled tega podjetja kazalnik ne bi bil prikazan',
    iskanje: 'Iskanje vrstic, npr. stroški storitev',
    namigMreze: 'Klik na celico jo vpiše v formulo. Puščica ▸ pokaže druge različice vrstice.',
    posebnost: 'posebnost podjetja – ni na voljo v kazalniku za vsa podjetja',
    razpri: (naziv) => `Druge različice: ${naziv}`,
    vstavi: (koordinata) => `Vpiši ${koordinata} v formulo`,
    odseki: {
      ipi: 'Izkaz poslovnega izida',
      bs: 'Bilanca stanja',
      cf: 'Denarni tok',
      vloge: 'Vloge vrstic',
      izracunane: 'Izračunane vrednosti',
    },
    enote: { odstotek: '%', krat: '×', meseci: 'meseci', eur: '€' },
    napaka: (vrsta, mesto, podrobnost) => {
      switch (vrsta) {
        case 'konec':
          return podrobnost === '' ? 'Formula se konča prezgodaj.' : `Formula se konča prezgodaj – manjka »${podrobnost}«.`;
        case 'pricakovan':
          return `Na mestu ${mesto} manjka »${podrobnost}«.`;
        case 'razpon':
          return 'Razpon (npr. A1:A5) ni dovoljen – naštejte celice: SUM(A1;A2;A5).';
        case 'stolpec':
          return `Stolpca ${podrobnost} ni – na voljo so A, B in C.`;
        case 'odvec':
          return `Odvečno besedilo od mesta ${mesto}: »${podrobnost}«.`;
        case 'stevilo':
          return `Število »${podrobnost}« je dvoumno – decimalna vejica je »,«, ločil tisočic ni.`;
        case 'celica':
          return `Celica ${mesto} ne obstaja ali ni izbirna.`;
        default:
          return `Nerazumljiv znak »${podrobnost}« na mestu ${mesto}.`;
      }
    },
    shraniNaslov: 'Shrani v katalog',
    koda: 'Koda',
    kodaNamig: 'Male črke, številke in podčrtaj; po shranjevanju se ne spremeni.',
    kodaNeveljavna: 'Koda se mora začeti z malo črko in imeti 2–50 znakov: male črke brez šumnikov, številke in podčrtaj.',
    nazivSl: 'Naziv (SL)',
    nazivEn: 'Naziv (EN)',
    opisSl: 'Kaj pove in česa ne (SL)',
    opisEn: 'Kaj pove in česa ne (EN)',
    skupina: 'Skupina na strani Pregled',
    dodajVPregled: 'Dodaj v pregled vseh podjetij (vsako ga lahko zase izklopi)',
    shrani: 'Shrani v katalog',
    shraniSpremembe: 'Shrani spremembe',
    shranjujem: 'Shranjujem …',
    shranjeno: (naziv) => `Kazalnik »${naziv}« je shranjen v katalog.`,
    nazajVKatalog: 'Nazaj v katalog',
    urejam: (naziv) => `Urejate: ${naziv}`,
    kopija: (naziv) => `${naziv} (kopija)`,
    niVMrezi:
      'Shranjene formule ni mogoče prikazati v mreži tega podjetja, ker katere od njenih vrstic tu ni. Izberite drugo podjetje za predogled.',
    niKazalnika: (koda) => `Kazalnika »${koda}« ni v katalogu.`,
    brezFormule: 'Ta kazalnik nima formule, ki bi jo lahko odprli v graditelju.',
    neprenesena: (formula) =>
      `Formule ${formula} ni mogoče prenesti na mrežo tega podjetja, ker katere od njenih vrstic tu ni. Da ne bi tiho spremenila pomena, je odstranjena – vpišite jo znova.`,
  },
  skupaj: 'skupaj',
  vsaObdobja: 'vsa obdobja',
  namigCelice: 'Prikaži, iz česa je sestavljeno',
  namigCelicaSkupaj: 'Prikaži knjižbe celotnega obdobja',
  dimenzija: 'Dimenzija',
  brezFiltra: 'Brez filtra',
  vrednost: 'Vrednost',
  vse: '(Vse)',
  prazno: '(prazno)',
  vecElementov: '(Več elementov)',
  izberiVecElementov: 'Izberi več elementov',
  isci: 'Išči',
  niZadetkov: 'Ni zadetkov.',
  vRedu: 'V redu',
  preklici: 'Prekliči',
  filterZa: (naziv) => `Filter: ${naziv}`,
  razclenitev: 'Razčlenitev',
  zapriRazclenitev: 'Zapri razčlenitev',
  beremKnjizbe: 'Berem knjižbe …',
  niKnjizb: 'V tem obdobju ni knjižb.',
  povzetek: (koliko, znesek) => `${String(koliko)} knjižb, skupaj ${znesek}`,
  prikazanihPrvih: (koliko) => ` — prikazanih prvih ${String(koliko)}.`,
  pokaziIzlocene: 'Prikaži tudi izločene knjižbe (zaključne temeljnice)',
  prenesiKnjizbe: 'Prenesi vse knjižbe v Excel',
  ponastaviSirine: 'Ponastavi širine stolpcev',
  namigSirine: 'Povlecite za širino stolpca, dvoklik prilagodi vsebini',
  sirinaStolpca: (stolpec) => `Širina stolpca ${stolpec}`,
  knjizbeNapaka: 'Napaka pri branju knjižb.',
  glavaDatum: 'datum',
  glavaKonto: 'konto',
  glavaDokument: 'dokument',
  glavaPartner: 'partner',
  glavaOpis: 'opis',
  glavaZnesek: 'znesek',
  izvirnaVrstica: 'Izvirna vrstica',
  pokaziIzvirnoVrstico: 'Pokaži izvirno vrstico iz datoteke uvoza',
  beremIzvirnoVrstico: 'Berem izvirno vrstico …',
  izvirnaVrsticaNapaka: 'Izvirne vrstice ni bilo mogoče prebrati.',
  virArhiv: 'arhiv – izvirna datoteka',
  virKnjizba: 'baza – shranjene izvorne vrednosti',
  lokacijaXlsx: (zavihek, vrstica) => `list »${zavihek}«, vrstica ${String(vrstica)}`,
  lokacijaCsv: (zapis) => `CSV, zapis ${String(zapis)} za glavo`,
  izvirnaDatoteka: 'Datoteka',
  glavaStolpec: 'stolpec',
  glavaVrednost: 'vrednost',
  stolpcaNiBilo: 'tega stolpca v vrstici ni bilo',
  zapriOkno: 'Zapri',
  niDostopa: 'Ni dostopa',
  straneUrejaServis: 'To stran ureja računovodski servis.',
};

const EN: Besedila = {
  znamka: 'Koda Reporting Portal',
  znamkaPrvi: 'Koda',
  znamkaDrugi: 'Reporting',
  porocila: 'Reports',
  glavniZavihki: 'Main sections',
  vsaPodjetja: 'All companies …',
  uvozInNastavitve: 'Import and settings',
  podnaslov: 'Financial reporting',
  podjetja: 'Companies',
  uporabniki: 'Users',
  posiljanja: 'Sent mail',
  arhiv: 'Archive',
  stanjeBaze: 'Database status',
  nastavitve: 'Settings',
  skrbnik: 'administrator',
  zamenjajGeslo: 'Change password',
  odjava: 'Sign out',
  staroGeslo: 'Current password',
  novoGeslo: 'New password (at least 10 characters)',
  zamenjaj: 'Change',
  gesloZamenjano: 'The password has been changed. Other devices are signed out.',
  gesloNiUspelo: 'The password could not be changed.',
  jezikOznaka: 'Language',
  berem: 'Loading …',
  prijava: 'Sign in',
  ePosta: 'E-mail',
  geslo: 'Password',
  prijavljam: 'Signing in …',
  prijavaNiUspela: 'Sign-in failed.',
  dostopOdpre: 'Access is granted by the accounting service. If you do not know your password, contact them.',
  nastavitevGesla: 'Set your password',
  vabiloNiVeljavno: 'The invitation is not valid or has expired.',
  vabiloZa: (email) => `Invitation for ${email}. Set the password you will sign in with.`,
  nastaviGeslo: 'Set password and sign in',
  shranjujem: 'Saving …',
  gesloNiMogoce: 'The password could not be set.',
  vabilaNiMogoce: 'The invitation could not be read.',
  novoGesloNaslov: 'New password',
  ponastavitevNiVeljavna: 'The password reset link is not valid or has expired. Ask your accounting office for a new one.',
  ponastavitevZa: (email) => `New password for ${email}. Saving it signs you out on all other devices.`,
  shraniNovoGeslo: 'Save new password and sign in',
  ponastavitveNiMogoce: 'The password reset link could not be read.',
  podjetijNiMogoce: 'The companies could not be read.',
  brezPodjetijSkrbnik: 'There is no company yet. Create one below and then upload a general ledger export.',
  brezPodjetijStranka: 'You do not have access to any company yet. Please contact the accounting service.',
  novoPodjetje: 'New company',
  naziv: 'Name',
  virKnjizb: 'Ledger source',
  uvozNiPodprt: (naziv) => `${naziv} (import not supported yet)`,
  ustvari: 'Create',
  podjetjaNiMogoce: 'The company could not be created.',
  podjetje: 'Company',
  poslovnoLeto: 'Financial year',
  brezKnjizb: 'There are no entries for this company yet.',
  naloziIzvoz: 'Upload a general ledger export',
  knjizb: 'entries',
  primerjalnoLeto: 'Comparative year',
  letoPorocila: 'Year',
  zamenjajLeto: 'Click the year to change the report year',
  odMeseca: 'From month',
  meseci: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  doMeseca: 'To month',
  vsiMeseci: '(All months)',
  meseciNaziv: 'Months',
  niPodatkov: 'no data',
  meseciZaporedni: 'Click the first and the last month – the months in between are selected automatically.',
  izberiKoncniMesec: 'Now click the last month.',
  skupina: 'Group',
  namigLeta: 'At most two years are available at a time: the latest and one comparative year.',
  porocilaZa: (leto) => `Reports for ${String(leto)}`,
  brezPorocil: 'No report is enabled for this company.',
  paketSpodaj: ' Set the package below.',
  podatki: 'Data',
  nastavitveRazvrscanja: 'Classification settings',
  namigNastavitve: 'Groups, account ranges and lines as they apply to this company.',
  nastavitveKazalnikov: 'Indicator settings',
  namigNastavitveKazalnikov: 'Gross margin accounts and the lines the indicators read from.',
  nastavitveFcVc: 'Fixed and variable costs',
  namigNastavitveFcVc: 'Variable share of costs by account for the sensitivity analysis.',
  nastavitvePartnerjev: 'Partners and gross margin by customer',
  namigNastavitvePartnerjev: 'Accounts for turnover by partner and document types for gross margin by customer.',
  projekti: 'Projects',
  nastavitevOdstotkov: 'Project percentages',
  namigNastavitevOdstotkov: 'Project shares per fixed asset for allocating depreciation.',
  projektiPregled: 'Project overview',
  namigProjektiPregled: 'Depreciation used by project, remaining amount and usage by month.',
  nastavitveProjektov: 'Project settings',
  namigNastavitveProjektov: 'Project list, fixed-asset register, journal entry and month closing.',
  letNiMogoce: 'The years could not be read.',
  opisiPorocil: {
    hitri: 'Simplified overview',
    rezultat: 'Monthly movements',
    'bs-s': 'Month-end balances',
    bs: 'Opening balance and changes',
    cf: 'Monthly cash flow',
    primerjava: 'This year and last year',
    kazalniki: 'Key indicators, sensitivity',
    partnerji: 'Largest customers and suppliers',
    'rvc-kupci': 'Gross margin by customer',
  },
  napacenNaslov: 'Wrong report address.',
  racunamPorocilo: 'Calculating the report …',
  porociloNiMogoce: 'The report could not be read.',
  namigPorocila:
    'Click an amount to see the entries it is made of — including on a group and on the total row. Click a name to collapse or expand a level.',
  izvoziPorocilo: 'Export the report to Excel',
  izvoziVsa: 'Export all reports (each on its own sheet)',
  pokaziPodrobneKonte: 'Show detailed accounts',
  postavka: 'Line',
  kazalnik: 'Indicator',
  sn: {
    splosneNastavitve: 'General settings',
    naslov: 'Indicators',
    podnaslov:
      'Applies to all companies. An empty field means the default value from the application; only changes are stored, so an application update does not overwrite them. A company can enable or disable an indicator for itself on its Indicator settings page.',
    beremNastavitve: 'Reading settings …',
    napakaBranja: 'The settings could not be read.',
    napakaShranjevanja: 'The setting could not be saved.',
    shrani: 'Save',
    shranjujem: 'Saving …',
    shranjeno: 'Saved.',
    ponastavi: 'Reset to default',
    izklopljen: 'disabled',
    izklopljeno: 'disabled',
    prilagojeno: 'customised',
    formula: 'Formula',
    formulaNiUrejljiva: 'the formula is part of the application and is not edited here',
    ustvari: '+ Create your own indicator',
    urediVGraditelju: 'Edit in the builder',
    kopiraj: 'Copy and adjust',
    kopirajNamig: 'The built-in calculation stays; the copy is a new indicator with a formula you can change.',
    izbrisi: 'Delete indicator',
    izbrisiPotrdi: (naziv) => `The indicator “${naziv}” will be deleted together with its settings for all companies. Delete it?`,
    izbrisiDa: 'Yes, delete',
    motorVgrajen: 'built-in calculation',
    motorFormula: 'formula',
    izGraditelja: 'built in the builder',
    vklopZaVse: 'Enabled for all companies',
    vklop: 'Enabled',
    privzetoVklopljen: (v) => `Default (${v ? 'enabled' : 'disabled'})`,
    privzetoVklopljeno: (v) => `Default (${v ? 'enabled' : 'disabled'})`,
    vklopljen: 'Enabled',
    vklopljeno: 'Enabled',
    izklopljenIzbira: 'Disabled',
    izklopljenoIzbira: 'Disabled',
    vrstniRed: 'Order',
    vrstniRedNapaka: 'The order must be a whole number.',
    nazivSl: 'Name (SL)',
    nazivEn: 'Name (EN)',
    opisSl: 'Description – "What does it mean?" (SL)',
    opisEn: 'Description – "What does it mean?" (EN)',
    naslovSl: 'Title (SL)',
    naslovEn: 'Title (EN)',
    besediloSl: 'Text (SL)',
    besediloEn: 'Text (EN)',
    stopnja: 'Severity (affects the order)',
    privzetoStopnja: (s) => `Default (${s})`,
    vrstaTrend: 'comparison with last year',
    vrstaRaven: 'value against threshold',
    pravilaOpozoril: 'Warning rules',
    nadomestki: (seznam) => `Placeholders allowed in the text: ${seznam}. Numbers are inserted in Slovenian format.`,
    brezNadomestkov: 'none',
    pragNapaka: (prag) => `${prag}: enter a number (decimal comma, no thousands separators).`,
    izkljuciGa: (p) => `Not shown when ${p} fires (a disabled rule does not exclude).`,
    izkljuci: (p) => `When it fires, ${p} is not shown.`,
    pragovi: {
      prag: 'Threshold',
      mocnejsi_prag: 'Stronger threshold (severity "warning")',
      najmanj_mesecev: 'Minimum months in the period',
      razmerje_pod: 'Ratio below (%)',
      povecanje: 'Increase compared with last year',
      najmanjse_razmerje: 'Minimum ratio',
      poslabsanje_mesecev_prihodkov: 'Decrease (share of average monthly net sales)',
      razlika_rasti: 'Growth difference (percentage points)',
      najmanj_mesecev_prihodkov: 'Minimum months of revenue',
      najmanj_mesecev_prometa: 'Minimum months of turnover',
    },
  },
  pa: {
    namig: 'Click an amount to see the partner’s entries; last year is the same period of the comparison year.',
    mesto: 'Rank',
    partner: 'Partner',
    niPartnerjev: 'No entries on the selected accounts in the selected period.',
    brezTabel: 'No table for the selected period.',
    izbiraKontov: 'Account selection',
    privzete: 'Default',
    poMeri: 'Custom …',
    konti: 'Accounts',
    namigKontov: 'Account prefixes separated by commas, e.g. 22, 23 or 7600.',
    vnesiKonto: 'Enter at least one account (digits only).',
    napacniKonti: (konti) => `Invalid account: ${konti}. An account has 1 to 6 digits, e.g. 22 or 7600.`,
    razvrstitev: 'Order',
    najboljsiRvc: 'Highest margin',
    najslabsiRvc: 'Lowest margin',
    vrstaDokumenta: 'Document type',
    knjizb: 'Entries',
    prodajnaVrednost: 'Sales value',
    nabavnaVrednost: 'Cost of goods',
    brezVrste: '(no type)',
    stejeVRvc: 'Counts in margin',
    nacinVrst: 'Which documents count in the margin',
    vsiDokumenti: 'All documents (including purchases)',
    samoIzbrane: 'Selected types only',
    izberiVrsto: 'Select at least one document type or “All documents”.',
    vrsteNaKontihNamig:
      'Document types found in entries on the gross margin accounts (all imported years). Tick sales and cost of goods sold; not purchases (e.g. received invoices) – they carry the supplier, not the customer.',
    niVrstNaKontih: 'There are no entries on the gross margin accounts yet.',
    napakaVrstice: (vrstica, opis) => `Preset “${vrstica}”: ${opis}`,
    stran: 'Side',
    strani: { breme: 'debit', dobro: 'credit' },
    pokazi: 'Show',
    prikazi: 'Show',
    prvih: (n) => `top ${String(n)}`,
    nastavitve: 'Tab settings',
    naslov: 'Partners and gross margin by customer',
    podnaslov:
      'The Partners tab sums the selected side of entries on the selected accounts by partner. Presets are quick choices above the table; the ones open by default (at most two) show without a selection. Gross margin by customer reads the gross margin accounts from the indicator settings; document types are set here.',
    samoBranje: 'Settings can be changed by the company administrator or the accounting firm.',
    prednastavitve: 'Partners tab presets',
    standardne: 'The company uses the default presets of the standard scheme. Once you save, yours apply.',
    lastne: 'The company has its own presets.',
    naziv: 'Name',
    privzeta: 'Open by default',
    namigPrivzeta: 'At most two presets can be open by default.',
    dodaj: 'Add preset',
    odstrani: 'Remove',
    shrani: 'Save',
    vrniPrivzete: 'Restore defaults',
    shranjeno: 'Saved.',
    napakaBranja: 'The settings could not be read.',
    napakaShranjevanja: 'The settings could not be saved.',
    vrsteNaslov: 'Gross margin by customer: document types',
    vrsteNamig:
      'Document types that count towards gross margin by customer, separated by commas (in Pantheon e.g. 420 issued invoice and 481 cost of goods sold). Empty means all documents.',
    vrste: 'Document types',
    kontiRvc: (prodajna, nabavna) => `Gross margin accounts (indicator settings): sales value ${prodajna}, cost of goods ${nabavna}.`,
    brezKontovRvc: 'The gross margin accounts are not set yet; the accounting firm sets them in the indicator settings.',
  },
  ob: {
    zavihek: 'Sensitivity',
    obdobje: 'Period',
    celoLeto: (leto) => `Last full year (${String(leto)})`,
    tekoceLeto: (obdobje) => `Current year (${obdobje})`,
    podnaslovCelo: (leto) => `Last full year ${String(leto)} (1 Jan – 31 Dec)`,
    podnaslovTekoce: (obdobje) => `${obdobje}, not annualised`,
    nerazvrsceni: (odstotek, znesek) =>
      `${odstotek} of costs (${znesek}) are not yet split into fixed and variable. They are treated as fixed, so the results are approximate. We recommend reviewing the split with your advisor.`,
    nadPragomNaslov: 'The analysis is not set up yet',
    nadPragomBesedilo: (prag) =>
      `More than ${prag} of this period's costs are not yet split into fixed and variable, so the break-even estimate and the matrix are not shown. Your advisor sets up the split.`,
    kontrola: (ebitda, cd) =>
      `The calculation cannot be shown: the inputs do not reconcile with the income statement (EBITDA difference ${ebitda}, total profit ${cd}). Please inform your advisor.`,
    niPodatkov: 'There is no data for the sensitivity analysis.',
    tockaNaslov: 'Estimated break-even point',
    tocka: 'Estimated break-even point',
    cistiPrihodki: 'Net sales for the period',
    rezerva: 'Safety margin',
    cistihPrihodkov: (odstotek) => `${odstotek} of net sales`,
    stavekNiMogoce: 'The break-even point cannot be estimated: variable costs are equal to or higher than sales.',
    stavekPokrito:
      'Under the assumptions of this model, other income and financial and other items already cover the fixed part; the estimated break-even point is €0.',
    stavekPrimanjkljaj: (znesek) => `Net sales would need to be higher by ${znesek} to reach break-even.`,
    stavekRezerva: (odstotek) => `The safety margin is ${odstotek} of net sales for the period.`,
    opomba:
      'The estimate is based on splitting costs into fixed and variable; it includes depreciation and financial and other income and expenses.',
    legendaPrihodki: 'sales',
    legendaKritje: 'required coverage',
    legendaFiksni: 'fixed part (net)',
    podGrafom:
      'Required coverage = fixed costs, depreciation and financial and other items, less other income, plus variable costs.',
    tockaNaGrafu: 'Break-even',
    dejanskiPrihodki: 'actual sales',
    grafOpis: 'Break-even chart: sales and required coverage against net sales',
    poglejIzracun: 'Show calculation',
    skrijIzracun: 'Hide calculation',
    izracunCp: 'Net sales (CP)',
    izracunV: 'Variable costs (V)',
    izracunCm: 'Contribution per €1 of sales (1 − V / CP)',
    izracunFe: 'Fixed costs in EBITDA',
    izracunAm: '+ depreciation',
    izracunOr: '− other operating income',
    izracunPoslovni: 'operating income',
    izracunOdstetiCp: '− net sales',
    celicaIzhodisce: 'baseline: net sales for the period',
    grafPokrito:
      'Required coverage is below sales at every level, so the lines do not cross; the estimated break-even point is €0.',
    izracunDfIzguba: '+ loss from financing and other activities',
    izracunDfDobicek: '− profit from financing and other activities',
    izracunF: '= fixed part (F)',
    izracunBe: 'Break-even = F / contribution',
    izracunKontrola: 'Check: CP × contribution − F = total profit',
    matrikaNaslov: 'EBITDA matrix',
    strosek: 'Cost',
    strosekIzbran: (naziv, delez) => `${naziv} (variable part ${delez})`,
    vseFiksno: 'The selected cost is entirely fixed; a change in its unit price has no effect.',
    nacinKolicina: 'Volume change',
    nacinCena: 'Price change',
    razlagaKolicina: 'We sell more or less at the same prices; variable costs change proportionally.',
    razlagaCena: 'We sell the same quantity at a higher or lower price; costs stay the same.',
    osKolicina: 'Change in sales volume',
    osCena: 'Change in selling price',
    osStroska: (naziv) => `Change in unit price: ${naziv}`,
    izhodisce: 'baseline',
    podMatriko: (ebitda) =>
      `EBITDA for the period, with the difference to the baseline (${ebitda}) below. A unit price change affects only the variable part of the selected cost. Click a cell to see the calculation.`,
    celicaNaslov: (vrstica, stolpec) => `Cell calculation: sales ${vrstica}, cost price ${stolpec}`,
    celicaPrihodki: (sprememba) => `Net sales × (1 ${sprememba})`,
    celicaOr: '+ other operating income',
    celicaFiksni: '− fixed part of costs',
    celicaOstali: '− variable part of other costs',
    celicaIzbrani: '− variable part of the selected cost',
    celicaEbitda: '= EBITDA',
    stroskiPoKontih: 'Costs by account',
    konto: 'Account',
    naziv: 'Name',
    znesek: 'Amount',
    vc: 'VC',
    nerazvrsceno: 'not split',
    zapri: 'Close',
    variabilniDel: (delez) => `VC ${delez}`,
  },
  pk: {
    pregled: 'Overview',
    podrobno: 'Details',
    kljucniZneski: 'Key figures',
    kazalniki: 'Indicators',
    vsiKazalniki: 'All indicators and tables',
    kajPomeni: 'What does it mean?',
    pogledIzracun: 'See the calculation',
    skrijIzracun: 'Close the calculation',
    letos: 'This year',
    lani: 'Last year',
    razlika: 'Difference',
    formula: 'Formula',
    sestavine: 'Components',
    poMesecih: 'By month',
    grafPrihodkov: 'Revenue by month',
    grafDobicka: 'Total profit by month',
    grafDobickaPodnaslov: (leto, primerjalno) =>
      primerjalno === null ? `Bar ${String(leto)}` : `Bar ${String(leto)}, dash ${String(primerjalno)}`,
    dobicek: 'profit',
    izguba: 'loss',
    denarniTokPodnaslov: 'Cash at month end and flows by activity, in EUR',
    zacetekLeta: '1 Jan',
    stanjeDenarja: 'Cash balance',
    razlikaVCeniPodnaslov: 'Profit margin by month',
    marzaObdobja: 'Period margin',
    kontiRazlike: (nabavna, prodajna) => `Accounts set for the company: purchase ${nabavna} · sales ${prodajna}`,
    podrobnoNoga: (datum) =>
      `Figures come from the general ledger, entries as at ${datum}. Year-closing journals set for the company are excluded from the income statement. Clicking a number opens the entries it is made of.`,
    kratkaPrimerjava: 'Short comparison with last year',
    ni: '—',
    brezKazalnikov: 'There is no data for indicators for this year.',
    proti: (kaj) => `vs ${kaj}`,
    meseci: 'mo.',
    krat: '×',
    namigTocke: (mesec, leto, znesek) => `${mesec} ${String(leto)}: ${znesek} — click to see the entries`,
    odstotneTocke: 'pp',
    gibanje: 'Movement',
    gibanjeGlavno: 'Balance growth vs turnover growth',
    gibanjeRazlika: (razlika) => `difference ${razlika}`,
    gibanjeStanja: 'Balance movement',
    gibanjeUvod: (obdobjeZdaj, obdobjePrej, rastStanja, promet, rastPrometa) =>
      `The average month-end balance changed by ${rastStanja} in ${obdobjeZdaj} compared with ${obdobjePrej}, and ${promet} by ${rastPrometa}.`,
    gibanjeRazmerje: (razlika, smer) =>
      smer === 'enako'
        ? 'Both changed equally: relative to turnover, the balance is the same as last year.'
        : `The difference between the two changes is ${razlika}: relative to turnover, the balance is ${smer === 'vecje' ? 'higher' : 'lower'} than last year.`,
    gibanjeNaDan: (datumZdaj, datumPrej, rast, razlika) =>
      `The balance on the last day (${datumZdaj}) changed by ${rast} compared with ${datumPrej}${razlika === null ? '' : ` (${razlika} relative to turnover growth)`}.`,
    gibanjeLeto: (odZacetka, lani) =>
      [odZacetka === null ? null : `Since the start of the year the balance has changed by ${odZacetka}`, lani === null ? null : `last year over the whole year by ${lani}`]
        .filter((d) => d !== null)
        .join('; ') + '.',
    gibanjeStolpci: { postavka: 'Item', prej: 'Before', zdaj: 'Now', sprememba: 'Change', protiPrometu: 'Relative to turnover growth' },
    kakoSeRacuna: 'How it is calculated',
    gibanjePomen: (g) => [
      `${g.povprecje.naziv}: growth of the average of month-end balances, this year and last year over the same months, compared with the growth of turnover (${g.nazivPrometa}). The large number is the difference between the two growth rates in percentage points. An average is not sensitive to a single day – for example a large payment just before month-end – so this is the main figure.`,
      `${g.stanje.naziv}: the balance on the last day of the period against the same day last year, and the difference to turnover growth. It is shown because you can check it in the balance sheet; a single day can, however, change the picture considerably.`,
      `${g.odZacetka.naziv}: how the balance has changed this year, from the opening balance to the last month. With seasonal business the change can be large without any particular reason.`,
      `${g.lani.naziv}: how the balance changed over the whole of last year, from the opening balance to 31 December – to judge whether this year's movement is usual.`,
    ],
    kajPove: 'What it tells and what it does not',
    mesec: 'Month',
    zaPregled: 'For review',
    niSignalov: 'No signals for review.',
    preveri: 'Check',
    steviloSignalov: (n) => `${String(n)} ${n === 1 ? 'signal' : 'signals'}`,
    nsPodnaslov: (obdobje, primerjalno) =>
      primerjalno === null ? obdobje : `${obdobje}, growth against the same period of ${String(primerjalno)}`,
    nsDelez: (delez) => `${delez} % of operating revenue`,
    nsStrosek: 'Cost',
    nsPostavka: 'Item',
    nsRast: 'Growth',
    nsDelezStolpec: 'Share of revenue',
    nsCeloLeto: (leto, seznam) => (seznam === '' ? `Whole year ${String(leto)}` : `Whole year ${String(leto)}: ${seznam}`),
    nsPrihodkiRast: (rast) => `revenue ${rast}`,
    nsDelezKratko: (delez) => `${delez} % of revenue`,
    enotaMeseci: 'months',
    laniMalo: 'last year',
    dniVezave: 'days',
    dniVezaveLani: (dni) => `last year ${dni}`,
    projekcijaNaLeto: 'annual projection',
    gibanjePovprecnoStanje: 'Average balance',
    primerjavaDo: (n) => `Comparison to ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][n - 1] ?? String(n)}`,
    primerjavaPodnaslov: (leto, primerjalno) =>
      primerjalno === null
        ? `${String(leto)}, year to date.`
        : `${String(leto)} vs ${String(primerjalno)}, same period. A green arrow means a better result, a red one a worse result – for costs too.`,
    nogaOpomba: 'Clicking a number opens the entries it is made of. Amounts on the tiles are rounded to whole euros.',
    zaPregledOpomba: 'Indicators are an aid for reviewing the business, not an assessment of the company.',
    vec: 'More',
    manj: 'Less',
    pokaziRazlago: 'Show explanation',
    skrijRazlago: 'Hide explanation',
    in: 'and',
    glavaPodnaslov: (obdobje, primerjalno, stanje) =>
      `${obdobje}${primerjalno === null ? '' : `, comparative year ${String(primerjalno)}`} · entries as at ${stanje}`,
    primerjava: 'Comparison',
    izvozVExcel: 'Export to Excel',
    uvPodnaslov: (obdobje) => `How gross output is divided down to the amount left for development · ${obdobje}`,
    uvPodnaslovKratek: 'From gross output to the amount left for development',
    uvOpisGrafa: 'Value creation: gross output, value added, EBIT and the amount left for development',
    uvKosmategaDonosa: 'of gross output',
    uvDodaneVrednosti: 'of value added',
    uvDelezDodane: (odstotek) => `${odstotek} of value added`,
    uvPrimanjkljaj: 'Shortfall',
    uvIzguba: 'Operating loss',
    uvBrezGrafa: 'The split cannot be drawn because value added or one of its parts is negative. The amounts are in the calculation.',
    uvStavek: (razvoj, odstotek, odplacila, primanjkljaj) =>
      primanjkljaj
        ? `Debt service ${odplacila} exceeds EBIT: shortfall ${razvoj} (${odstotek} of value added).`
        : `Left for development ${razvoj} (${odstotek} of value added). Debt service ${odplacila}.`,
    uvStavekIzguba: (ebit) => `EBIT is negative (${ebit}): an operating loss, nothing is left for debt service and development.`,
    uvOdplacila: (odplacila, obresti, glavnice) => `Debt service ${odplacila}: interest ${obresti} · principal ${glavnice}`,
    uvLegenda: {
      blago: 'Goods, materials, services',
      dodana: 'Value added, EBIT',
      delo: 'Labour, debt service',
      amortizacija: 'Depreciation',
      razvoj: 'Left for development',
    },
    uvIzracun: 'Calculation',
    uvPrenosi:
      'Principal is the debit turnover on financial liability accounts without transfers between the non-current and current part within one journal. A transfer is calculated per journal, so it is not a sum of entries: the debit turnover opens the entries, transfers are listed by journal.',
    uvTemeljnica: (temeljnica) => `Journal ${temeljnica}`,
    uvNeprepoznaniPrenosi: (znesek) =>
      `The data source has no journal number, so transfers between the non-current and current part are not recognised and are counted as principal. Such transfers can amount to at most ${znesek}.`,
  },
  gr: {
    naslov: 'Indicator builder',
    podnaslov:
      'Write the formula as in Excel or build it by clicking cells. Column A is the current period, B the same period of the comparative year, C the end of the comparative year. The result is calculated for the selected company as you type.',
    odpri: 'Indicator builder',
    nazaj: 'Indicators',
    podjetje: 'Preview for company',
    leto: 'Year',
    primerjalno: 'Comparative year',
    samoPredogled: 'The company is used only for the preview. The indicator applies to all companies.',
    beremMrezo: 'Loading rows …',
    brezPodjetij: 'There are no companies for the preview.',
    brezLet: 'The company has no imported entries.',
    stolpecC: 'Show column C (end of the comparative year)',
    formula: 'Formula',
    namigFormule:
      'Allowed: + − * /, parentheses and SUM(A1;A3;A8) with listed cells (no ranges). Clicking a cell inserts its reference at the cursor. Decimal comma, e.g. 0,5.',
    placeholder: '=A7/A1*100  or click a cell in the table',
    prikaz: 'Display',
    enota: 'Unit',
    decimalke: 'Decimals',
    imenovalecPozitiven: 'The divisor must be positive (otherwise there is no value)',
    skrijNic: 'Hide the indicator when all balances in the formula are zero',
    projekcijaOd: 'Annual projection from month',
    pomen: 'Cells in the formula',
    predogled: 'Preview',
    racunam: 'Calculating …',
    vpisiFormulo: 'Enter a formula or click a cell in the table.',
    niPrikazan: 'The indicator would not be shown on the Overview page of this company',
    iskanje: 'Search rows, e.g. services',
    namigMreze: 'Clicking a cell inserts it into the formula. The arrow ▸ shows other variants of the row.',
    posebnost: 'company-specific row – not available in an indicator for all companies',
    razpri: (naziv) => `Other variants: ${naziv}`,
    vstavi: (koordinata) => `Insert ${koordinata} into the formula`,
    odseki: {
      ipi: 'Income statement',
      bs: 'Balance sheet',
      cf: 'Cash flow',
      vloge: 'Row roles',
      izracunane: 'Calculated values',
    },
    enote: { odstotek: '%', krat: '×', meseci: 'months', eur: '€' },
    napaka: (vrsta, mesto, podrobnost) => {
      switch (vrsta) {
        case 'konec':
          return podrobnost === '' ? 'The formula ends too early.' : `The formula ends too early – “${podrobnost}” is missing.`;
        case 'pricakovan':
          return `“${podrobnost}” is missing at position ${mesto}.`;
        case 'razpon':
          return 'A range (e.g. A1:A5) is not allowed – list the cells: SUM(A1;A2;A5).';
        case 'stolpec':
          return `There is no column ${podrobnost} – columns A, B and C are available.`;
        case 'odvec':
          return `Extra text from position ${mesto}: “${podrobnost}”.`;
        case 'stevilo':
          return `The number “${podrobnost}” is ambiguous – the decimal separator is “,” and there are no thousands separators.`;
        case 'celica':
          return `Cell ${mesto} does not exist or cannot be selected.`;
        default:
          return `Unexpected character “${podrobnost}” at position ${mesto}.`;
      }
    },
    shraniNaslov: 'Save to the catalogue',
    koda: 'Code',
    kodaNamig: 'Lowercase letters, digits and underscore; it does not change after saving.',
    kodaNeveljavna: 'The code must start with a lowercase letter and have 2–50 characters: lowercase letters without diacritics, digits and underscore.',
    nazivSl: 'Name (SL)',
    nazivEn: 'Name (EN)',
    opisSl: 'What it tells and what not (SL)',
    opisEn: 'What it tells and what not (EN)',
    skupina: 'Group on the Overview page',
    dodajVPregled: 'Add to the overview of all companies (each can switch it off)',
    shrani: 'Save to the catalogue',
    shraniSpremembe: 'Save changes',
    shranjujem: 'Saving …',
    shranjeno: (naziv) => `The indicator “${naziv}” is saved to the catalogue.`,
    nazajVKatalog: 'Back to the catalogue',
    urejam: (naziv) => `Editing: ${naziv}`,
    kopija: (naziv) => `${naziv} (copy)`,
    niVMrezi:
      "The saved formula cannot be shown in this company's grid because one of its rows is not there. Choose another company for the preview.",
    niKazalnika: (koda) => `There is no indicator “${koda}” in the catalogue.`,
    brezFormule: 'This indicator has no formula that could be opened in the builder.',
    neprenesena: (formula) =>
      `The formula ${formula} cannot be carried over to this company's grid because one of its rows is not there. So that its meaning does not change silently, it has been removed – please enter it again.`,
  },
  skupaj: 'total',
  vsaObdobja: 'all periods',
  namigCelice: 'Show what this is made of',
  namigCelicaSkupaj: 'Show the entries of the whole period',
  dimenzija: 'Dimension',
  brezFiltra: 'No filter',
  vrednost: 'Value',
  vse: '(All)',
  prazno: '(blank)',
  vecElementov: '(Multiple items)',
  izberiVecElementov: 'Select multiple items',
  isci: 'Search',
  niZadetkov: 'No matches.',
  vRedu: 'OK',
  preklici: 'Cancel',
  filterZa: (naziv) => `Filter: ${naziv}`,
  razclenitev: 'Breakdown',
  zapriRazclenitev: 'Close the breakdown',
  beremKnjizbe: 'Loading entries …',
  niKnjizb: 'There are no entries in this period.',
  povzetek: (koliko, znesek) => `${String(koliko)} entries, total ${znesek}`,
  prikazanihPrvih: (koliko) => ` — showing the first ${String(koliko)}.`,
  pokaziIzlocene: 'Also show excluded entries (closing journals)',
  prenesiKnjizbe: 'Download all entries to Excel',
  ponastaviSirine: 'Reset column widths',
  namigSirine: 'Drag to resize the column, double-click to fit its content',
  sirinaStolpca: (stolpec) => `Width of column ${stolpec}`,
  knjizbeNapaka: 'Error reading the entries.',
  glavaDatum: 'date',
  glavaKonto: 'account',
  glavaDokument: 'document',
  glavaPartner: 'partner',
  glavaOpis: 'description',
  glavaZnesek: 'amount',
  izvirnaVrstica: 'Source row',
  pokaziIzvirnoVrstico: 'Show the source row from the import file',
  beremIzvirnoVrstico: 'Reading the source row …',
  izvirnaVrsticaNapaka: 'The source row could not be read.',
  virArhiv: 'archive – original file',
  virKnjizba: 'database – stored source values',
  lokacijaXlsx: (zavihek, vrstica) => `sheet "${zavihek}", row ${String(vrstica)}`,
  lokacijaCsv: (zapis) => `CSV, record ${String(zapis)} after the header`,
  izvirnaDatoteka: 'File',
  glavaStolpec: 'column',
  glavaVrednost: 'value',
  stolpcaNiBilo: 'the row had no such column',
  zapriOkno: 'Close',
  niDostopa: 'No access',
  straneUrejaServis: 'This page is maintained by the accounting service.',
};

const SLOVARJI: Record<Jezik, Besedila> = { sl: SL, en: EN };

export const JezikContext = createContext<Jezik>(PRIVZETI_JEZIK);

/** Jezik, v katerem je vmesnik. */
export function useJezik(): Jezik {
  return useContext(JezikContext);
}

/** Besedila vmesnika v izbranem jeziku. */
export function useBesedila(): Besedila {
  return SLOVARJI[useJezik()];
}

/** Besedila v podanem jeziku; za strani pred prijavo. */
export function besedila(jezik: Jezik): Besedila {
  return SLOVARJI[jezik];
}

/**
 * Jezik pred prijavo.
 *
 * Prijave in sprejema vabila ne vidi prijavljen uporabnik, zato nastavitve ni;
 * takrat odloci brskalnik. Po prijavi velja nastavitev uporabnika.
 */
export function jezikBrskalnika(): Jezik {
  const prvi = typeof navigator === 'undefined' ? '' : (navigator.language ?? '');
  return vJezik(prvi.slice(0, 2).toLowerCase());
}
